import { useState, useEffect } from "react";
import { Search, Check, ChevronDown, Bot } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/ui-components/ui/dropdown-menu";
import { AGENT_CONFIG } from "@/lib/constants";
import type { ConnectedTool } from "@/hooks/useConnectedTools";
import { useFilters } from "@/hooks/useFilters";

interface MultiSelectAgentDropdownProps {
  agents: ConnectedTool[];
  className?: string;
  triggerClassName?: string;
  includeAllOption?: boolean;
  allOptionLabel?: string;
}

export function MultiSelectAgentDropdown({
  agents = [],
  includeAllOption = true,
  allOptionLabel = "All Agents",
  triggerClassName,
  className,
}: MultiSelectAgentDropdownProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { agentNames, toggleAgentName, resetAgentsFilter } = useFilters();

  // Add "All Agents" option if requested
  const allAgents = includeAllOption
    ? [
        {
          id: "all",
          name: allOptionLabel,
          logo: "",
          lastUsed: "",
          sessionCount: 0,
          docsUrl: "",
        },
        ...agents,
      ]
    : agents;

  // Check if "all" is selected
  const isAllSelected = agentNames.includes("all");

  // Get display text for trigger button
  const getDisplayText = () => {
    if (isAllSelected || agentNames.length === 0) {
      return allOptionLabel;
    }
    if (agentNames.length === 1) {
      const agent = allAgents.find((a) => a.id === agentNames[0]);
      return agent?.name || "";
    }
    return `${agentNames.length} agents`;
  };

  // Get the icon to display
  const getDisplayIcon = () => {
    if (isAllSelected || agentNames.length === 0 || agentNames.length > 1) {
      return null;
    }
    const agent = allAgents.find((a) => a.id === agentNames[0]);
    if (!agent || agent.id === "all") return null;

    // Get the icon from AGENT_CONFIG
    const agentConfig = AGENT_CONFIG[agent.id as keyof typeof AGENT_CONFIG];
    if (agentConfig?.icon) {
      return (
        <img
          src={agentConfig.icon}
          alt={agent.name}
          className="w-3.5 h-3.5 rounded"
        />
      );
    }

    return null;
  };

  // Handle selection changes
  const handleSelect = (agentId: string) => {
    try {
      if (agentId === "all") {
        // If selecting "all", clear other selections
        resetAgentsFilter();
      } else {
        // Remove "all" if it was selected
        toggleAgentName(agentId);
      }
    } catch (error) {
      console.error("Error in handleSelect:", error);
    }
  };

  // Clear all selections
  const handleClearAll = () => {
    resetAgentsFilter();
    setSearchQuery("");
  };

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  // Filter agents based on search
  const filteredAgents = allAgents.filter((agent) =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Count selected (excluding "all")
  const selectedCount = isAllSelected ? 0 : agentNames.length;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={
            triggerClassName +
            `
            ${
              agentNames.length > 0
                ? "border border-border text-foreground hover:border-border hover:bg-muted"
                : "text-muted-foreground hover:bg-muted"
            }
          `
          }
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {getDisplayIcon() || <Bot className="w-3.5 h-3.5" />}
          <span>{getDisplayText()}</span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={`${className || "w-[250px]"} z-[100]`}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Prevent closing when clicking on filter menu elements
          const target = e.target as HTMLElement;
          if (target.closest(".filter-menu-container")) {
            e.preventDefault();
          }
        }}
      >
        {allAgents.length > 10 && (
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-[#01B9D9] focus:border-transparent"
                onMouseDown={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
        )}

        {selectedCount > 0 && (
          <div className="border-b border-border p-3 flex items-center justify-between space-x-6">
            <span className="text-xs text-muted-foreground">
              {selectedCount} agent{selectedCount !== 1 ? "s" : ""} selected
            </span>
            <button
              onMouseDown={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-xs text-[#01B9D9] hover:text-[#0095B6] font-medium"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="max-h-[300px] overflow-y-auto">
          {filteredAgents.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">No agents found</p>
              <p className="text-xs text-muted-foreground mt-1">
                Try a different search
              </p>
            </div>
          ) : (
            filteredAgents.map((agent) => {
              const isSelected =
                agent.id === "all"
                  ? isAllSelected
                  : agentNames.includes(agent.id);

              const agentConfig =
                AGENT_CONFIG[agent.id as keyof typeof AGENT_CONFIG];

              return (
                <button
                  key={agent.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleSelect(agent.id);
                    // Only close if selecting "all"
                    if (agent.id === "all") {
                      setIsOpen(false);
                    }
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center justify-between group"
                >
                  <span className="flex items-center gap-2">
                    {agent.id !== "all" && agentConfig?.icon && (
                      <img
                        src={agentConfig.icon}
                        alt={agent.name}
                        className="w-4 h-4 rounded"
                      />
                    )}
                    <span
                      className={
                        isSelected
                          ? "font-medium text-[#01B9D9]"
                          : "text-foreground"
                      }
                    >
                      {agent.name}
                    </span>
                  </span>
                  {isSelected && (
                    <Check className="w-3.5 h-3.5 text-[#01B9D9]" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
